import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiMessageCircle, FiImage, FiVideo, FiMusic, FiCalendar, FiUsers, FiTrash2, FiUpload, FiPlus, FiX } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { campanhasAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const TipoButtons = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const TipoButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border: 2px solid ${props => props.$selected ? theme.colors.primary[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$selected ? theme.colors.primary[500] : theme.colors.background.primary};
  color: ${props => props.$selected ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background: ${props => props.$selected ? theme.colors.primary[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${props => props.$hasFile ? theme.colors.success[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$hasFile ? `${theme.colors.success[500]}10` : theme.colors.background.primary};

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.background.secondary};
  }
`;

const FileUploadIcon = styled.div`
  font-size: ${theme.fontSizes['3xl']};
  color: ${props => props.$hasFile ? theme.colors.success[500] : theme.colors.text.muted};
  margin-bottom: ${theme.spacing[2]};
`;

const FileUploadText = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0;
  font-size: ${theme.fontSizes.sm};
`;

const FileUploadHint = styled.p`
  color: ${theme.colors.text.muted};
  margin: ${theme.spacing[1]} 0 0 0;
  font-size: ${theme.fontSizes.xs};
`;

const HiddenInput = styled.input`
  display: none;
`;

const ContatosManuais = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ContatoItem = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const ContatoInput = styled(Input)`
  flex: 1;
`;

const RemoveButton = styled.button`
  padding: ${theme.spacing[2]};
  border: 1px solid ${theme.colors.error[500]};
  background: transparent;
  color: ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${theme.colors.error[500]};
    color: ${theme.colors.text.primary};
  }
`;

const AddContatoButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px dashed ${theme.colors.primary[500]};
  background: transparent;
  color: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[500]}20;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewContainer = styled.div`
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: ${theme.borderRadius.md};
`;

const PreviewVideo = styled.video`
  max-width: 100%;
  max-height: 200px;
  border-radius: ${theme.borderRadius.md};
`;

const PreviewAudio = styled.audio`
  width: 100%;
`;

const CharCounter = styled.div`
  text-align: right;
  color: ${props => {
    const percentage = (props.$current / props.$max) * 100;
    if (percentage >= 100) return theme.colors.error[500];
    if (percentage >= 90) return theme.colors.warning[500];
    return theme.colors.text.muted;
  }};
  font-size: ${theme.fontSizes.xs};
  margin-top: ${theme.spacing[1]};
`;

const CampanhaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'text',
    conteudo_texto: '',
    origem_contatos: 'todos',
    contatos_manuais: [],
    agendado_para: '',
    id_cliente: ''
  });

  const [arquivo, setArquivo] = useState(null);
  const [arquivoPreview, setArquivoPreview] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isEditing) {
      carregarCampanha();
    }
  }, [id]);

  useEffect(() => {
    carregarClientes();
  }, []);

  // Função para converter UTC para formato datetime-local (horário local)
  const utcToLocalDateTime = (utcString) => {
    if (!utcString) return '';
    const date = new Date(utcString);
    // Obter componentes da data no fuso local
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Função para obter data/hora mínima em formato local
  const getMinLocalDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const carregarCampanha = async () => {
    try {
      setLoadingData(true);
      const response = await campanhasAPI.buscarPorId(id);
      
      if (response.data.success) {
        const campanha = response.data.data;
        setFormData({
          nome: campanha.nome || '',
          descricao: campanha.descricao || '',
          tipo: campanha.tipo || 'text',
          conteudo_texto: campanha.conteudo_texto || '',
          origem_contatos: campanha.origem_contatos || 'todos',
          contatos_manuais: campanha.contatos_manuais || [],
          agendado_para: campanha.agendado_para ? utcToLocalDateTime(campanha.agendado_para) : '',
          id_cliente: campanha.id_cliente || ''
        });
        
        if (campanha.conteudo_url) {
          setArquivoPreview(campanha.conteudo_url);
        }
      } else {
        setError('Erro ao carregar dados da campanha');
      }
    } catch (err) {
      console.error('Erro ao carregar campanha:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const carregarClientes = async () => {
    try {
      const response = await campanhasAPI.listarClientes();
      if (response.data.success) {
        setClientes(response.data.data);
        if (response.data.data.length === 1 && !formData.id_cliente) {
          setFormData(prev => ({ ...prev, id_cliente: response.data.data[0].id }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
    }
  };

  const handleInputChange = (field, value) => {
    // Limitar texto a 4000 caracteres quando for mensagem
    if (field === 'conteudo_texto' && formData.tipo === 'text') {
      if (value.length > 4000) {
        value = value.substring(0, 4000);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTipoChange = (tipo) => {
    setFormData(prev => ({
      ...prev,
      tipo,
      conteudo_texto: tipo === 'text' ? prev.conteudo_texto : ''
    }));
    if (tipo === 'text') {
      setArquivo(null);
      setArquivoPreview(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArquivo(file);
      
      if (formData.tipo === 'image' || formData.tipo === 'video') {
        const url = URL.createObjectURL(file);
        setArquivoPreview(url);
      } else if (formData.tipo === 'audio') {
        const url = URL.createObjectURL(file);
        setArquivoPreview(url);
      }
    }
  };

  const handleAddContato = () => {
    setFormData(prev => ({
      ...prev,
      contatos_manuais: [...prev.contatos_manuais, { telefone: '', nome: '' }]
    }));
  };

  const handleContatoChange = (index, field, value) => {
    setFormData(prev => {
      const novosContatos = [...prev.contatos_manuais];
      novosContatos[index] = { ...novosContatos[index], [field]: value };
      return { ...prev, contatos_manuais: novosContatos };
    });
  };

  const handleRemoveContato = (index) => {
    setFormData(prev => ({
      ...prev,
      contatos_manuais: prev.contatos_manuais.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!formData.nome.trim()) {
        throw new Error('Nome é obrigatório');
      }

      if (!formData.id_cliente) {
        throw new Error('Cliente é obrigatório');
      }

      if (formData.tipo === 'text' && !formData.conteudo_texto.trim()) {
        throw new Error('Conteúdo de texto é obrigatório');
      }

      if (formData.tipo !== 'text' && !arquivo && !arquivoPreview) {
        throw new Error(`Arquivo de ${formData.tipo} é obrigatório`);
      }

      if (formData.origem_contatos === 'lista_manual' && formData.contatos_manuais.length === 0) {
        throw new Error('Adicione pelo menos um contato na lista manual');
      }

      const data = new FormData();
      data.append('nome', formData.nome.trim());
      data.append('descricao', formData.descricao?.trim() || '');
      data.append('tipo', formData.tipo);
      data.append('origem_contatos', formData.origem_contatos);
      data.append('id_cliente', formData.id_cliente);

      if (formData.tipo === 'text') {
        data.append('conteudo_texto', formData.conteudo_texto.trim());
      }

      if (arquivo) {
        data.append('arquivo', arquivo);
      }

      if (formData.origem_contatos === 'lista_manual') {
        data.append('contatos_manuais', JSON.stringify(formData.contatos_manuais));
      }

      if (formData.agendado_para) {
        // Converter de datetime-local (horário local) para UTC
        // O input datetime-local retorna uma string no formato YYYY-MM-DDTHH:mm
        // que é interpretada como horário local pelo construtor Date
        const localDate = new Date(formData.agendado_para);
        data.append('agendado_para', localDate.toISOString());
      }

      let response;
      if (isEditing) {
        response = await campanhasAPI.atualizar(id, data);
      } else {
        response = await campanhasAPI.criar(data);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'Campanha atualizada com sucesso!' : 'Campanha criada com sucesso!');
        setTimeout(() => {
          navigate('/campanhas');
        }, 1500);
      } else {
        setError(response.data.error || 'Erro ao salvar campanha');
      }
    } catch (err) {
      console.error('Erro ao salvar campanha:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/campanhas');
  };

  const handleExcluirCampanha = async () => {
    try {
      setDeleting(true);
      setError(null);

      await campanhasAPI.deletar(id);
      navigate('/campanhas');
    } catch (err) {
      console.error('Erro ao excluir campanha:', err);
      setError('Erro ao excluir campanha');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getAcceptedFileTypes = () => {
    switch(formData.tipo) {
      case 'image': return 'image/jpeg,image/png,image/gif,image/webp';
      case 'video': return 'video/mp4,video/quicktime,video/webm';
      case 'audio': return 'audio/mpeg,audio/ogg,audio/wav,audio/mp4,audio/aac';
      default: return '';
    }
  };

  const getFileTypeLabel = () => {
    switch(formData.tipo) {
      case 'image': return 'Imagem (JPG, PNG, GIF, WebP)';
      case 'video': return 'Vídeo (MP4, WebM, MOV)';
      case 'audio': return 'Áudio (MP3, OGG, WAV, M4A)';
      default: return '';
    }
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Campanha' : 'Nova Campanha'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar Campanha' : 'Nova Campanha'}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiMessageCircle />
              Informações da Campanha
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="nome">
                  Nome <Required>*</Required>
                </Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Nome da campanha"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="id_cliente">
                  Cliente <Required>*</Required>
                </Label>
                <Select
                  id="id_cliente"
                  value={formData.id_cliente}
                  onChange={(e) => handleInputChange('id_cliente', e.target.value)}
                  required
                >
                  <option value="">Selecione um cliente</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nome_fantasia || cliente.nome_social}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroupFull>
                <Label htmlFor="descricao">Descrição</Label>
                <TextArea
                  id="descricao"
                  placeholder="Descrição opcional da campanha"
                  value={formData.descricao}
                  onChange={(e) => handleInputChange('descricao', e.target.value)}
                />
              </FormGroupFull>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiMessageCircle />
              Tipo de Conteúdo
            </SectionTitle>

            <FormGroup>
              <Label>Tipo <Required>*</Required></Label>
              <TipoButtons>
                <TipoButton
                  type="button"
                  $selected={formData.tipo === 'text'}
                  onClick={() => handleTipoChange('text')}
                >
                  <FiMessageCircle />
                  Texto
                </TipoButton>
                <TipoButton
                  type="button"
                  $selected={formData.tipo === 'image'}
                  onClick={() => handleTipoChange('image')}
                >
                  <FiImage />
                  Imagem
                </TipoButton>
                <TipoButton
                  type="button"
                  $selected={formData.tipo === 'video'}
                  onClick={() => handleTipoChange('video')}
                >
                  <FiVideo />
                  Vídeo
                </TipoButton>
                <TipoButton
                  type="button"
                  $selected={formData.tipo === 'audio'}
                  onClick={() => handleTipoChange('audio')}
                >
                  <FiMusic />
                  Áudio
                </TipoButton>
              </TipoButtons>
            </FormGroup>

            {formData.tipo === 'text' && (
              <FormGroupFull>
                <Label htmlFor="conteudo_texto">
                  Mensagem <Required>*</Required>
                </Label>
                <TextArea
                  id="conteudo_texto"
                  placeholder="Digite a mensagem que será enviada..."
                  value={formData.conteudo_texto}
                  onChange={(e) => handleInputChange('conteudo_texto', e.target.value)}
                  maxLength={4000}
                  style={{ minHeight: '150px' }}
                />
                <CharCounter $current={formData.conteudo_texto.length} $max={4000}>
                  {formData.conteudo_texto.length} / 4000 caracteres
                </CharCounter>
              </FormGroupFull>
            )}

            {formData.tipo !== 'text' && (
              <FormGroupFull>
                <Label>
                  Arquivo <Required>*</Required>
                </Label>
                <label>
                  <FileUploadArea $hasFile={!!arquivo || !!arquivoPreview}>
                    <FileUploadIcon $hasFile={!!arquivo || !!arquivoPreview}>
                      <FiUpload />
                    </FileUploadIcon>
                    <FileUploadText>
                      {arquivo ? arquivo.name : (arquivoPreview ? 'Arquivo carregado' : 'Clique para selecionar ou arraste o arquivo')}
                    </FileUploadText>
                    <FileUploadHint>{getFileTypeLabel()}</FileUploadHint>
                  </FileUploadArea>
                  <HiddenInput
                    type="file"
                    accept={getAcceptedFileTypes()}
                    onChange={handleFileChange}
                    multiple={false}
                  />
                </label>

                {arquivoPreview && (
                  <PreviewContainer>
                    {formData.tipo === 'image' && (
                      <PreviewImage src={arquivoPreview} alt="Preview" />
                    )}
                    {formData.tipo === 'video' && (
                      <PreviewVideo src={arquivoPreview} controls />
                    )}
                    {formData.tipo === 'audio' && (
                      <PreviewAudio src={arquivoPreview} controls />
                    )}
                  </PreviewContainer>
                )}
              </FormGroupFull>
            )}
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiUsers />
              Destinatários
            </SectionTitle>

            <FormGroup>
              <Label>Origem dos Contatos <Required>*</Required></Label>
              <Select
                value={formData.origem_contatos}
                onChange={(e) => handleInputChange('origem_contatos', e.target.value)}
              >
                <option value="todos">Todos os Contatos</option>
                <option value="favoritos">Contatos Favoritos</option>
                <option value="lista_manual">Nova Lista (Manual)</option>
              </Select>
            </FormGroup>

            {formData.origem_contatos === 'lista_manual' && (
              <ContatosManuais>
                <Label>Lista de Contatos</Label>
                {formData.contatos_manuais.map((contato, index) => (
                  <ContatoItem key={index}>
                    <ContatoInput
                      type="text"
                      placeholder="Telefone (ex: 5511999999999)"
                      value={contato.telefone}
                      onChange={(e) => handleContatoChange(index, 'telefone', e.target.value)}
                    />
                    <ContatoInput
                      type="text"
                      placeholder="Nome (opcional)"
                      value={contato.nome}
                      onChange={(e) => handleContatoChange(index, 'nome', e.target.value)}
                    />
                    <RemoveButton type="button" onClick={() => handleRemoveContato(index)}>
                      <FiX />
                    </RemoveButton>
                  </ContatoItem>
                ))}
                <AddContatoButton type="button" onClick={handleAddContato}>
                  <FiPlus />
                  Adicionar Contato
                </AddContatoButton>
              </ContatosManuais>
            )}
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiCalendar />
              Agendamento
            </SectionTitle>

            <FormGroup>
              <Label htmlFor="agendado_para">Data e Hora do Envio</Label>
              <Input
                id="agendado_para"
                type="datetime-local"
                value={formData.agendado_para}
                onChange={(e) => handleInputChange('agendado_para', e.target.value)}
                min={getMinLocalDateTime()}
              />
            </FormGroup>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            {isEditing && (
              <Button type="button" onClick={() => setShowDeleteModal(true)} $danger>
                <ButtonIcon>
                  <FiTrash2 />
                </ButtonIcon>
                Excluir
              </Button>
            )}
            <Button $primary type="submit" disabled={loading}>
              <ButtonIcon>
                {loading ? <FiLoader /> : <FiSave />}
              </ButtonIcon>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir Campanha</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir a campanha <strong>{formData.nome}</strong>? Esta ação não pode ser desfeita.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleExcluirCampanha} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default CampanhaForm;

